/**
 * Card / Panel — the recurring surface primitive.
 *
 * A rounded, subtly-shadowed container on the warm-neutral surface palette.
 * `title` renders a standard panel header; `actions` sits on the right of that
 * header (e.g. a Refresh button or a filter). Use `padded={false}` for tables
 * that should bleed to the card edge.
 */
import type { CSSProperties, ReactNode } from 'react';
import { colors, radius, shadow } from '../theme';

export interface CardProps {
  title?: ReactNode;
  /** Right-aligned content in the title row (buttons, filters). */
  actions?: ReactNode;
  children: ReactNode;
  padded?: boolean;
  /** Use the warm surface fill instead of white (e.g. right-context cards). */
  surface?: boolean;
  style?: CSSProperties;
}

export function Card({
  title,
  actions,
  children,
  padded = true,
  surface = false,
  style,
}: CardProps) {
  return (
    <section
      style={{
        background: surface ? colors.surface : colors.white,
        border: `1px solid ${colors.border}`,
        borderRadius: radius.md,
        boxShadow: surface ? 'none' : shadow.sm,
        ...style,
      }}
    >
      {(title || actions) && (
        <header style={styles.header}>
          {typeof title === 'string' ? <h3 style={styles.title}>{title}</h3> : title}
          {actions && <div style={styles.actions}>{actions}</div>}
        </header>
      )}
      <div style={padded ? styles.body : undefined}>{children}</div>
    </section>
  );
}

const styles: Record<string, CSSProperties> = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '14px 18px',
    borderBottom: `1px solid ${colors.surfaceAlt}`,
  },
  title: { margin: 0, fontSize: '1rem', fontWeight: 600, color: colors.textPrimary },
  actions: { display: 'flex', alignItems: 'center', gap: 8 },
  body: { padding: 18 },
};
